import { SelfContainedCommand, CommandExecutionContext } from '../../../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../../types/standardToolResults';
import { entityCreationRegistry } from '../../../../services/entityCreationRegistry';
import { createFileLogger } from '../../../../utils/logger';
const log = createFileLogger('INBOXITEM_CREATE');

export const inboxitemCreateCommand: SelfContainedCommand = {
  id: 'inboxitem_create',
  description: `Surface an AI output or notification in the user's Inbox.

Use after a skill run, morning briefing, or any significant agentic output that the user
should be able to review at their own pace.

**When to use:**
- After completing a complex skill run (morning briefing, report, analysis)
- When you need to notify the user about something while they're away
- To deliver structured output that the user can reference later

**When NOT to use:**
- For inline chat responses — just reply directly
- For quick confirmations ("Done!") — say it in chat

**EXAMPLES:**

Morning briefing completion:
{ "title": "Morning Briefing — Mar 2", "content": "3 urgent emails from key contacts. 2 meetings today at 10am and 2pm.", "sourceEntityId": "skill_morning_briefing_abc", "priority": "high" }

Weekly report:
{ "title": "Weekly Summary — Week of Mar 2", "content": "You closed 4 tasks, sent 12 emails, and made progress on 2 goals.", "priority": "normal" }`,
  autonomyLevel: 'auto',
  visualTreatment: 'silent',

  schema: {
    parameters: [
      {
        name: 'spaceId',
        type: 'string',
        description: 'Space ID to create the InboxItem in (uses current space if not provided)',
        required: false,
      },
      {
        name: 'title',
        type: 'string',
        description: 'Short title for the inbox item (e.g. "Morning Briefing — Jan 24")',
        required: true,
      },
      {
        name: 'preview',
        type: 'string',
        description: 'Preview text shown in the inbox list (first ~200 chars of the output)',
        required: true,
      },
      {
        name: 'channel',
        type: 'string',
        description: 'Source channel: "ai_output" (default) | "notification"',
        required: false,
        defaultValue: 'ai_output',
      },
      {
        name: 'fromName',
        type: 'string',
        description: 'Display name of the source (e.g. "Morning Briefing Skill")',
        required: false,
        defaultValue: 'River',
      },
      {
        name: 'fromIcon',
        type: 'string',
        description: 'Emoji icon for the source (e.g. "🌅" for morning briefing)',
        required: false,
      },
      {
        name: 'priority',
        type: 'string',
        description: '"high" | "normal" (default) | "low"',
        required: false,
        defaultValue: 'normal',
      },
      {
        name: 'payloadEntityId',
        type: 'string',
        description: 'Entity ID of the full output (e.g. the Page doc with the full briefing)',
        required: false,
      },
      {
        name: 'sourceType',
        type: 'string',
        description: '"skill_run" | "agent_run" | "goal_analysis" | "system"',
        required: false,
        defaultValue: 'skill_run',
      },
      {
        name: 'sourceEntityId',
        type: 'string',
        description: 'Entity ID of the source (e.g. the Skill entity that ran)',
        required: false,
      },
      {
        name: 'actions',
        type: 'array',
        description: `Action buttons rendered in the inbox item detail pane. Each action object:
  - { label, type: "open_entity", entityId }  — opens an entity in a window
  - { label, type: "run_command", commandId, commandParams? } — runs a registered command
  - { label, type: "link", url } — opens a URL in a new tab
  - { label, type: "agent_prompt", prompt, confirmText? } — fires a background agent job with the given prompt; confirmText is shown on the button while running (e.g. "Merging…")

AGENT_PROMPT EXAMPLE (merge a PR):
{ "label": "Merge PR", "type": "agent_prompt", "confirmText": "Merging…", "prompt": "Merge pull request #42 in org/repo using the GitHub tool. After merging, create an inbox item confirming success." }`,
        required: false,
      },
    ],
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const {
      spaceId: providedSpaceId,
      title,
      preview,
      channel = 'ai_output',
      fromName = 'River',
      fromIcon,
      priority = 'normal',
      payloadEntityId,
      sourceType = 'skill_run',
      sourceEntityId,
      actions = [],
    } = params;

    const spaceId = providedSpaceId || context.spaceId;

    try {
      log.info('[inboxitem_create] Creating InboxItem:', { title, channel, spaceId });

      const now = new Date().toISOString();

      const entityStoreModule = await import('../../../../stores/entityStore');
      const entityStore = entityStoreModule.default.getState();

      const newItem = await entityStore.actions.createEntity({
        entityType: 'InboxItem',
        name: title,
        spaceId,
        skeleton: {
          '@type': 'InboxItem',
          channel,
          title,
          preview: preview?.slice(0, 500) || '',
          fromName,
          fromIcon: fromIcon || null,
          timestamp: now,
          state: 'unread',
          priority,
          sourceType,
          sourceEntityId: sourceEntityId || null,
          payloadEntityId: payloadEntityId || null,
          actions: actions || [],
        },
      } as any);

      const entityId = newItem._id || newItem.id;
      log.info('[inboxitem_create] ✅ InboxItem created:', entityId);

      // Dispatch entity:created event so live InboxApp instances update immediately
      document.dispatchEvent(
        new CustomEvent('entity:created', {
          detail: { entity: { ...newItem, spaceId } },
          bubbles: true,
        })
      );

      return createSuccessResult(`InboxItem created: "${title}"`, { entityId, title });
    } catch (error: any) {
      log.error('[inboxitem_create] Error:', error);
      return createErrorResult(
        'Failed to create InboxItem',
        error.message || 'Unknown error',
        'execution'
      );
    }
  },
};

entityCreationRegistry.register({
  commandId: 'inboxitem_create',
  entityType: 'InboxItem',
  displayName: 'Inbox Item',
  icon: { type: 'phosphor', phosphorIcon: 'Tray', phosphorWeight: 'duotone' },
  description: 'AI output or notification in the Inbox',
  canCreateInFolder: false,
  openAfterCreate: false,
  category: 'system',
  sortOrder: 99,
});
